import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { apiService } from '../../services/api';
import { UserRole } from '../../../../shared/types';
import { useAuth } from '../../contexts/AuthContext';
import { CheckCircle, XCircle, AlertCircle, Users, Shield, HardDrive } from 'lucide-react';

interface UserManagementState {
  users: UserRole[];
  loading: boolean;
  error: string | null;
  updatingUserId: string | null;
  successMessage: string | null;
}

export const UserManagementPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [state, setState] = useState<UserManagementState>({
    users: [],
    loading: true,
    error: null,
    updatingUserId: null,
    successMessage: null,
  });

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const response = await apiService.listUsers();
      setState(prev => ({ 
        ...prev, 
        users: response.users, 
        loading: false 
      }));
    } catch (error) {
      console.error('Failed to load users:', error);
      setState(prev => ({ 
        ...prev, 
        loading: false, 
        error: 'Failed to load users. Please try again.' 
      }));
    }
  };

  const updateUserRoles = async (userId: string, systemAdmin: boolean, storageAdmin: boolean) => {
    try {
      setState(prev => ({ 
        ...prev, 
        updatingUserId: userId, 
        error: null, 
        successMessage: null 
      }));

      await apiService.updateUserRoles(userId, {
        systemAdmin,
        storageAdmin,
      });

      // Update the user in the local state
      setState(prev => ({
        ...prev,
        users: prev.users.map(user =>
          user.userId === userId
            ? { ...user, systemAdmin, storageAdmin }
            : user
        ),
        updatingUserId: null,
        successMessage: 'User roles updated successfully',
      }));

      // Clear success message after 3 seconds
      setTimeout(() => {
        setState(prev => ({ ...prev, successMessage: null }));
      }, 3000);

    } catch (error) {
      console.error('Failed to update user roles:', error);
      setState(prev => ({ 
        ...prev, 
        updatingUserId: null,
        error: 'Failed to update user roles. Please try again.' 
      }));
    }
  };

  const handleRoleChange = (userId: string, roleType: 'systemAdmin' | 'storageAdmin', checked: boolean) => {
    const user = state.users.find(u => u.userId === userId);
    if (!user) return;

    const newSystemAdmin = roleType === 'systemAdmin' ? checked : user.systemAdmin;
    const newStorageAdmin = roleType === 'storageAdmin' ? checked : user.storageAdmin;

    updateUserRoles(userId, newSystemAdmin, newStorageAdmin);
  };

  const getRoleDisplay = (user: UserRole) => {
    const roles = [];
    if (user.systemAdmin) roles.push('System Admin');
    if (user.storageAdmin) roles.push('Storage Admin');
    return roles.length > 0 ? roles.join(', ') : 'User';
  };

  const getRoleIcon = (user: UserRole) => {
    if (user.systemAdmin) return <Shield className="h-4 w-4 text-red-500" />;
    if (user.storageAdmin) return <HardDrive className="h-4 w-4 text-blue-500" />;
    return <Users className="h-4 w-4 text-gray-500" />;
  };

  if (!currentUser?.isSystemAdmin) {
    return (
      <Layout>
        <div className="py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-center">
                <XCircle className="h-6 w-6 text-red-500 mr-3" />
                <div>
                  <h3 className="text-lg font-medium text-red-800">Access Denied</h3>
                  <p className="text-red-700 mt-1">
                    You need System Administrator privileges to access user management.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center">
              <Users className="h-8 w-8 mr-3 text-primary-600" />
              User Management
            </h1>
            <p className="text-gray-600 mt-2">
              Manage user roles and permissions for the Audio Archive System
            </p>
          </div>

          {/* Success Message */}
          {state.successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                <p className="text-green-800">{state.successMessage}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {state.error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <XCircle className="h-5 w-5 text-red-500 mr-2" />
                <p className="text-red-800">{state.error}</p>
                <button
                  onClick={loadUsers}
                  className="ml-auto text-red-600 hover:text-red-800 underline"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {state.loading ? (
            <div className="bg-white shadow rounded-lg p-8">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                <p className="ml-3 text-gray-600">Loading users...</p>
              </div>
            </div>
          ) : (
            /* Users Table */
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">
                  System Users ({state.users.length})
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Manage administrative roles for system users
                </p>
              </div>

              {state.users.length === 0 ? (
                <div className="p-8 text-center">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No users found</p>
                  <button
                    onClick={loadUsers}
                    className="mt-2 text-primary-600 hover:text-primary-800 underline"
                  >
                    Refresh
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Current Role
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          System Admin
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Storage Admin
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Modified
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {state.users.map((user) => (
                        <tr key={user.userId} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8">
                                <div className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center">
                                  <span className="text-sm font-medium text-white">
                                    {user.email.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900">
                                  {user.email}
                                </div>
                                <div className="text-sm text-gray-500">
                                  ID: {user.userId.substring(0, 8)}...
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {getRoleIcon(user)}
                              <span className="ml-2 text-sm text-gray-900">
                                {getRoleDisplay(user)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={user.systemAdmin}
                                disabled={state.updatingUserId === user.userId}
                                onChange={(e) => handleRoleChange(user.userId, 'systemAdmin', e.target.checked)}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                              />
                              {state.updatingUserId === user.userId && (
                                <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={user.storageAdmin}
                                disabled={state.updatingUserId === user.userId}
                                onChange={(e) => handleRoleChange(user.userId, 'storageAdmin', e.target.checked)}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                              />
                              {state.updatingUserId === user.userId && (
                                <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {user.modifiedDate ? (
                              <div>
                                <div>{new Date(user.modifiedDate).toLocaleDateString()}</div>
                                {user.modifiedBy && (
                                  <div className="text-xs text-gray-400">
                                    by {user.modifiedBy.substring(0, 8)}...
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">Never</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Role Information */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-blue-500 mr-2 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-blue-800">Role Permissions</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <ul className="list-disc list-inside space-y-1">
                    <li><strong>System Admin:</strong> Can manage user roles and view budget dashboards</li>
                    <li><strong>Storage Admin:</strong> Can delete any user's files and folders</li>
                    <li><strong>User:</strong> Can manage their own files and folders</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};